import * as React from "react";
import { withTranslation, WithTranslation } from "react-i18next";
import { I18N_NS } from "Config/i18n";
import { observer } from "mobx-react";
import { ColumnProps } from "antd/lib/table";
import { SortOrder } from "antd/lib/table/interface";
import { TableColumnHeader } from "Components/General/ColumnHeaderTooltip";
import { ModalConsumer } from "Components/General/Modal/ModalContext";
import { Button } from "Components/General/Button";
import { stringSort } from "utils/defaultSort";
import { Loader } from "Components/General/Loader";
import { Row } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ViewsForm, ViewsFormProps } from "./ViewsForm";
import { API } from "General/API";
import { API_URL } from "General/APIEndpoints";
import { AccessRights } from "../Components/AccessRights";
import { projectsDataStore } from "Stores";
import { withRouter, WithRouterProps } from "Components/General/WithRouter";
import { Table } from "Components/General/Table";

interface TableSorter {
  columnKey: string;
  order: "ascend" | "descend";
}

interface ViewsProps extends WithTranslation, WithRouterProps {}

interface ViewsState {
  project: API_Project;
  views: API_View[];
  projectProviders: API_View[];
  loading: boolean;
  sortedInfo: TableSorter;
}

export const Views = withTranslation(I18N_NS.ADMIN)(
  withRouter(
    observer(
      class extends React.Component<ViewsProps, ViewsState> {
        private MODAL_PROPS = { width: `100%`, style: { maxWidth: 720 }, footer: null };

        constructor(props: ViewsProps) {
          super(props);
          this.state = {
            project: null,
            views: [],
            projectProviders: [],
            loading: true,
            sortedInfo: {
              columnKey: "selected",
              order: "ascend",
            },
          };
        }

        componentDidMount() {
          (async () => await this.handleFetch())();
        }

        handleFetch = async () => {
          try {
            this.setState({ loading: true });
            const projectId = +this.props.params.id;
            const project = await API.getData(API_URL.PROJECTS.getProject_1(projectId));
            projectsDataStore.setProjectId(projectId);
            this.setState({ project });
            await this.fetchViews(projectId);
          } catch (err) {
            throw err;
          } finally {
            this.setState({ loading: false });
          }
        };

        fetchViews = async (projectId: number) => {
          const views = await API.getData(API_URL.VIEWS.getAllViews(projectId));
          this.setState({ views });
        };

        handleCreate = async (obj, params: { allUsers: boolean }) => {
          try {
            const projectId = +this.props.params.id;
            await API.newData(API_URL.VIEWS.addNewView(projectId), obj, params);
            await this.fetchViews(projectId);
          } catch (error) {
            throw error;
          }
        };

        handleEdit = async (obj: Pick<API_View, "definitionXml" | "id">) => {
          try {
            const projectId = +this.props.params.id;
            await API.editData(API_URL.VIEWS.updateView(projectId, obj.id), obj);
            await this.fetchViews(projectId);
          } catch (error) {
            throw error;
          }
        };

        renderHeaderCell = (sortOrder: SortOrder = "ascend", title: string, columnIndex: string) => {
          const { t } = this.props;
          const { columnKey } = this.state.sortedInfo;

          return columnKey === columnIndex ? <TableColumnHeader title={t(title)} sortOrder={sortOrder} /> : t(title);
        };

        get viewsColumns(): ColumnProps<API_View>[] {
          const { t } = this.props;
          const { project } = this.state;

          return [
            {
              dataIndex: "name",
              title: "Name",
              sorter: (a, b) => stringSort(a, b, "name"),
              defaultSortOrder: "ascend",
            },
            {
              dataIndex: "filterMetadaten",
              title: t("Meta data column"),
              render: (text, record) => record.filterMetadaten.map((value, idx) => <div key={idx}>{value}</div>),
            },
            {
              dataIndex: "accessRights.metadaten",
              title: t("Meta data filter"),
              width: 380,
              render: (text, record) =>
                record.accessRights.metadaten.map((item, idx) => (
                  <div key={idx} style={{ wordWrap: "break-word", wordBreak: "break-all" }}>
                    {item.key} = {item.values.join(", ")} {item.beliebigerwert ? "*" : ""}
                  </div>
                )),
            },
            {
              dataIndex: "accessRights.fileTypes",
              title: t("File formats"),
              align: "center",
              render: (text, record) => record.accessRights.fileTypes.map((value, idx) => <div key={idx}>{value}</div>),
            },
            {
              dataIndex: "ignoreDirectoryRights",
              width: 140,
              align: "center",
              title: t("Ignore directory rights"),
              render: (text, record) => (
                <FontAwesomeIcon fixedWidth={true} icon={record.ignoreDirectoryRights ? "check" : "times"} size="1x" />
              ),
            },
            {
              dataIndex: "withFilesFromEmails",
              width: 105,
              align: "center",
              title: t("Include files from emails"),
              render: (text, record) => (
                <FontAwesomeIcon fixedWidth={true} icon={record.withFilesFromEmails ? "check" : "times"} size="1x" />
              ),
            },
            {
              dataIndex: "withDownloadAccess",
              width: 83,
              align: "center",
              title: t("Download"),
              render: (text, record) => (
                <FontAwesomeIcon fixedWidth={true} icon={record.withDownloadAccess ? "check" : "times"} size="1x" />
              ),
            },
            {
              dataIndex: "withPlotAccess",
              width: 70,
              align: "center",
              title: t("Print"),
              render: (text, record) => (
                <FontAwesomeIcon fixedWidth={true} icon={record.withPlotAccess ? "check" : "times"} size="1x" />
              ),
            },
            {
              dataIndex: "sendInfomailForNewFiles",
              align: "center",
              title: t("Auto-infomail"),
              render: (text, record) => (
                <FontAwesomeIcon fixedWidth={true} icon={record.sendInfomailForNewFiles ? "check" : "times"} />
              ),
            },
            {
              dataIndex: "actions",
              width: 70,
              align: "center",
              render: (text, record) => (
                <ModalConsumer>
                  {({ showModal }) => (
                    <>
                      <span
                        style={{ cursor: "pointer" }}
                        onClick={() =>
                          showModal<Pick<ViewsFormProps, "onEdit" | "project" | "viewId">>(ViewsForm, {
                            modalProps: { ...this.MODAL_PROPS, title: t("Edit view") },
                            componentProps: {
                              onEdit: this.handleEdit,
                              project: { id: project.id, name: project.name },
                              viewId: record.id,
                            },
                          })
                        }
                      >
                        <FontAwesomeIcon fixedWidth={true} icon={["fas", "edit"]} />
                      </span>

                      <span
                        style={{ cursor: "pointer" }}
                        onClick={() =>
                          showModal(AccessRights, {
                            modalProps: {
                              ...this.MODAL_PROPS,
                              title: t("Rights"),
                              width: "100%",
                              style: { maxWidth: 1200 },
                            },
                            componentProps: { viewId: record.id },
                          })
                        }
                      >
                        <FontAwesomeIcon fixedWidth={true} icon={["fas", "lock"]} />
                      </span>
                    </>
                  )}
                </ModalConsumer>
              ),
            },
          ];
        }

        render() {
          if (this.state.loading) {
            return <Loader />;
          }

          const { loading, project, views } = this.state;
          const { t } = this.props;

          return (
            <div className="g-card">
              <Row justify="space-between" className="mb-lg">
                <h1>
                  {t("Manage views")}: <strong>{project ? project.name : ""}</strong>
                </h1>
                <ModalConsumer>
                  {({ showModal }) => (
                    <div className="text-right">
                      <Button
                        type="primary"
                        faIcon={"plus"}
                        onClick={() =>
                          showModal<Pick<ViewsFormProps, "onCreate">>(ViewsForm, {
                            modalProps: {
                              ...this.MODAL_PROPS,
                              title: t("New view"),
                            },
                            componentProps: {
                              onCreate: this.handleCreate,
                            },
                          })
                        }
                      >
                        {t("New view")}
                      </Button>
                    </div>
                  )}
                </ModalConsumer>
              </Row>
              <Table<API_View>
                name="adminViews"
                rowKey="id"
                dataSource={views}
                loading={loading}
                columns={this.viewsColumns}
                onChange={this.handleChange}
              />
            </div>
          );
        }

        private handleChange = (pagination: {}, filters: {}, sorter: TableSorter) => {
          this.setState({
            sortedInfo: sorter,
          });
        };
      }
    )
  )
);
